/**
 * Moderation - everything a moderator does, in one place
 * -------------------------------------------------------
 * Only visible to moderators. For everybody else the API returns 404, so
 * there is nothing here to find. There is no separate admin login and there
 * should not be: it is a flag on an ordinary account, granted from the
 * command line with `npm run make-moderator`. A route that could hand out
 * moderator rights would be the most valuable thing on the site to attack.
 *
 * Four sections, kept apart because they are four different jobs:
 *
 *   Reports        things somebody has asked you to act on. Urgent first,
 *                  then oldest - a report that has sat for three days is a
 *                  worse problem than one that arrived this morning.
 *   Conversations  the ones you are allowed to read: reported, or where
 *                  contact details were shared. Not all of them, and the
 *                  server is what enforces that.
 *   Taken down     chores and offers currently removed, with the reason
 *                  given, and a button to put them back. It exists because a
 *                  takedown with no undo is a mistake nobody can fix.
 *   Audit log      what has already happened, including what you did.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, AlertTriangle, Ban, RotateCcw, Check, Flag, MessageSquare, ScrollText, EyeOff, LifeBuoy, MailWarning } from 'lucide-react';

import { safetyApi, settingsApi } from '../services/api';
import { timeAgo } from '../utils/format';
import Loader from '../components/Loader';
import EmptyState from '../components/EmptyState';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import ModerationThreads, { ThreadReader } from '../components/ModerationThreads';
import ModerationAudit from '../components/ModerationAudit';
import ModerationTakedowns from '../components/ModerationTakedowns';
import ModerationIssues from '../components/ModerationIssues';
import SuspendDialog from '../components/SuspendDialog';
import TakedownDialog from '../components/TakedownDialog';

import { useI18n } from '../hooks/useI18n';

const REASON_LABELS = {
  inappropriate_behaviour: 'moderation.reasonInappropriate',
  made_me_uncomfortable: 'moderation.reasonUnsafe',
  unsafe_work: 'moderation.reasonUnsafeWork',
  did_not_pay: 'Did not pay',
  did_not_turn_up: 'moderation.reasonNoShow',
  fake_profile: 'moderation.reasonFake',
  abusive_review: 'moderation.reasonAbusiveReview',
  something_else: 'moderation.reasonOther',
};

/**
 * THREE SECTIONS, NOT FOUR.
 *
 * "Conversations" used to be its own tab, which put the evidence for a
 * report one click away from the report and in a different list - so
 * reading a report meant leaving it, finding the right conversation among
 * all the others, and remembering which one you came from.
 *
 * A conversation is now opened from the report it belongs to. The ones
 * nobody reported - where only the contact-details detector fired - still
 * need somewhere to live, and that is the strip above the report list.
 *
 * With the four status tabs underneath, this page had eight tabs in two
 * rows. Five is a page you can look at.
 */
const SECTIONS = [
  { key: 'reports', label: 'moderation.tabReports', icon: Flag },
  { key: 'issues', label: 'moderation.tabIssues', icon: LifeBuoy },
  { key: 'takedowns', label: 'moderation.tabTakenDown', icon: EyeOff },
  { key: 'audit', label: 'moderation.tabAudit', icon: ScrollText },
];

const TABS = [
  { key: 'open', label: 'moderation.tabOpen' },
  { key: 'investigating', label: 'moderation.tabInvestigating' },
  { key: 'resolved', label: 'moderation.tabResolved' },
  { key: 'dismissed', label: 'moderation.tabDismissed' },
];

export default function Moderation() {
  const { t } = useI18n();
  const [section, setSection] = useState('reports');
  // The conversation a moderator has opened from a report, if any.
  const [readingThread, setReadingThread] = useState(null);
  const [tab, setTab] = useState('open');
  const [reports, setReports] = useState([]);
  const [counts, setCounts] = useState({ open: 0, urgentOpen: 0 });
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  /**
   * THE EMAIL SWITCH.
   *
   * Brevo gives this site 300 emails a day. When they are gone, every message
   * the site tries to send silently fails - and the failures land on exactly
   * the people who cannot work around them: a parent who never receives a
   * consent request, somebody locked out waiting for a reset link. Nothing
   * anywhere says why, so it looks like a broken site rather than a full
   * quota.
   *
   * This is the moderator saying "I know, and stop pretending". While it is
   * on, anything whose whole point is an email arriving is refused up front
   * with a plain explanation instead of accepted and then lost.
   *
   * Deliberately manual. Automatic detection would mean trusting an error
   * message from a mail server to mean what we think it means, and being
   * wrong in the direction of closing signups by accident.
   */
  const [emailsOffline, setEmailsOffline] = useState(false);
  const [switchingEmail, setSwitchingEmail] = useState(false);
  // What the switch actually does, hidden until asked for - see the note by
  // the banner below.
  const [explainEmail, setExplainEmail] = useState(false);

  const [acting, setActing] = useState(null); // { report, mode }
  const [outcome, setOutcome] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await safetyApi.reportQueue(tab);
      setReports(data.reports);
      setCounts(data.counts);
      setDenied(false);
    } catch (err) {
      if (err.status === 404) setDenied(true);
      else setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    settingsApi
      .read()
      .then((d) => setEmailsOffline(Boolean(d.emailsOffline)))
      .catch(() => setEmailsOffline(false));
  }, []);

  const toggleEmail = async () => {
    setSwitchingEmail(true);
    setError('');
    try {
      const { emailsOffline: now } = await settingsApi.setEmailsOffline(!emailsOffline);
      setEmailsOffline(now);
      setNotice(now ? t('moderation.emailNowOff') : t('moderation.emailNowOn'));
    } catch (err) {
      setError(err.message);
    } finally {
      setSwitchingEmail(false);
    }
  };

  const handle = async (status) => {
    setBusy(true);
    try {
      await safetyApi.handleReport(acting.report._id, status, outcome);
      setNotice(t('moderation.recorded'));
      setActing(null);
      setOutcome('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Which person the suspend dialog is open for, or null. It used to suspend
  // on the click, permanently, with a reason the code wrote itself - see
  // components/SuspendDialog.jsx for why both of those were wrong.
  const [suspending, setSuspending] = useState(null);

  // { kind: 'chore' | 'offer', id, name } while the takedown dialog is open.
  const [takingDown, setTakingDown] = useState(null);

  const takedown = async (reason) => {
    setBusy(true);
    try {
      const res =
        takingDown.kind === 'offer'
          ? await safetyApi.takedownOffer(takingDown.id, reason)
          : await safetyApi.takedownChore(takingDown.id, reason);
      setNotice(res.message);
      setTakingDown(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const suspend = async ({ reason, days, indefinite }) => {
    setBusy(true);
    try {
      const res = await safetyApi.suspendUser(suspending._id, { reason, days, indefinite });
      setNotice(res.message);
      setSuspending(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const reinstate = async (report) => {
    setBusy(true);
    try {
      const res = await safetyApi.reinstateUser(report.about._id);
      setNotice(res.message);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader label={t('moderation.loading')} />;

  if (denied) {
    return (
      <div className="container section">
        <EmptyState icon={ShieldAlert} title={t('moderation.notFound')} message={t('moderation.nothingHere')} />
      </div>
    );
  }

  return (
    <div className="container section">
      <div className="page-header">
        <h1>{t('moderation.title')}</h1>
        <p>
          {counts.urgentOpen > 0 ? (
            <strong className="text-error">
              {t('moderation.urgentWaiting', { count: counts.urgentOpen })}
            </strong>
          ) : (
            t('moderation.openWaiting', { count: counts.open })
          )}{' '}
          {t('moderation.call112')}
        </p>
      </div>

      {/* ---- The email switch ----
          At the top, above the tabs, because it is not one of the jobs on
          this page - it is the state of the whole site, and a moderator
          arriving because "nothing is working" should see it before they
          start reading reports. */}
      <div
        className={`alert ${emailsOffline ? 'alert-warning' : 'alert-info'} moderation-switch`}
        style={{ marginBottom: 'var(--space-md)' }}
      >
        <MailWarning size={18} aria-hidden="true" />

        {/* One line, and the button. The first version explained underneath
            what switching it off would do - four lines of it - which read
            fine on a laptop and filled a phone screen with an explanation of
            a button nobody presses most days. The detail is a tap away
            instead. */}
        <span className="moderation-switch-label">
          {emailsOffline ? t('moderation.emailOffTitle') : t('moderation.emailOnTitle')}
        </span>

        {/* Both buttons in one row, and `btn-inline` so they size to their
            own text. Without it a .btn fills its line, which put them on two
            separate rows and made a one-line notice three rows tall on a
            phone. */}
        <span className="row" style={{ gap: 'var(--space-xs)' }}>
          <button
            type="button"
            className={`btn btn-sm btn-inline ${emailsOffline ? 'btn-primary' : 'btn-outline'}`}
            onClick={toggleEmail}
            disabled={switchingEmail}
          >
            {emailsOffline ? t('moderation.emailTurnOn') : t('moderation.emailTurnOff')}
          </button>

          <button
            type="button"
            className="btn btn-ghost btn-sm btn-inline"
            onClick={() => setExplainEmail((open) => !open)}
            aria-expanded={explainEmail}
          >
            {explainEmail ? t('common.close') : t('moderation.emailWhat')}
          </button>
        </span>

        {explainEmail && (
          <p className="text-sm moderation-switch-detail">
            {emailsOffline ? t('moderation.emailOffBody') : t('moderation.emailOnBody')}
          </p>
        )}
      </div>

      {/* Four jobs, kept apart on purpose. Reports are things people have
          asked you to act on; conversations are things you may look at;
          taken-down is what has been removed and can be put back; the log is
          what has already happened, including what you did. */}
      <div className="tabs" style={{ marginBottom: 'var(--space-md)' }}>
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`tab${section === s.key ? ' active' : ''}`}
            onClick={() => setSection(s.key)}
          >
            <s.icon size={15} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 5 }} />
            {t(s.label)}
            {s.key === 'reports' && counts.open > 0 && (
              <span className="tab-count">{counts.open}</span>
            )}
          </button>
        ))}
      </div>

      {section === 'issues' && <ModerationIssues />}
      {section === 'takedowns' && <ModerationTakedowns />}
      {section === 'audit' && <ModerationAudit />}

      {/**
        * A conversation opened from a report takes over the page, with a way
        * back to the queue. A modal was the other option and is worse: these
        * are long transcripts being read carefully, and a box with its own
        * scrollbar inside a page that also scrolls is the wrong shape for
        * that - the message screen learned the same thing (section 13).
        */}
      {section === 'reports' && readingThread && (
        <ThreadReader
          choreId={readingThread.choreId}
          withStudent={readingThread.withStudent}
          offerId={readingThread.offerId}
          withPoster={readingThread.withPoster}
          onBack={() => setReadingThread(null)}
        />
      )}

      {section !== 'reports' || readingThread ? null : (
      <>
      {/**
        * The conversations nobody reported.
        *
        * These matter precisely BECAUSE nobody reported them - the
        * contact-details detector fires on "let us carry on over WhatsApp",
        * and the person being talked round does not think anything is wrong.
        * When Conversations was its own tab this was the only thing in it
        * that could not be reached any other way, so it keeps a home here.
        */}
      <ModerationThreads
        onlyUnreported
        onOpen={(which) => setReadingThread(which)}
      />

      {notice && <div className="alert alert-success" style={{ marginBottom: 'var(--space-sm)' }}>{notice}</div>}
      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-sm)' }}>{error}</div>}

      <div className="tabs">
        {/* `item`, not `t` - `t` is the translator, and naming the loop
            variable after it would shadow the thing we need to call. */}
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`tab${tab === item.key ? ' active' : ''}`}
            onClick={() => setTab(item.key)}
          >
            {t(item.label)}
            {item.key === 'open' && counts.open > 0 && <span className="tab-count">{counts.open}</span>}
          </button>
        ))}
      </div>

      {reports.length === 0 ? (
        <EmptyState icon={Check} title={t('moderation.nothingWithStatus')} message={t('moderation.noReportsWithStatus')} />
      ) : (
        <div className="stack" style={{ gap: 'var(--space-sm)' }}>
          {reports.map((r) => {
            const reporterIsMinor = r.reporter?.role === 'student' && r.reporter?.age < 18;
            return (
              <div
                className="card"
                key={r._id}
                style={r.severity === 'urgent' ? { borderColor: 'var(--color-error)', borderWidth: 2 } : undefined}
              >
                <div className="card-header">
                  <div>
                    <div className="row" style={{ gap: 'var(--space-xs)', marginBottom: 6 }}>
                      {r.severity === 'urgent' && (
                        <Badge tone="red" icon={AlertTriangle}>
                          {t('moderation.urgent')}
                        </Badge>
                      )}
                      <Badge tone="soft-amber">{REASON_LABELS[r.reason] ? t(REASON_LABELS[r.reason]) : r.reason}</Badge>
                      {reporterIsMinor && <Badge tone="amber">{t('misc.reportedByMinor')}</Badge>}
                      {r.about && !r.about.isActive && <Badge tone="grey">{t('misc.alreadySuspended')}</Badge>}
                    </div>
                    <h3 style={{ fontSize: 'var(--text-h4)' }}>
                      {t('moderation.aboutLabel')}{' '}
                      {r.about ? (
                        <Link to={`/${r.about.role === 'student' ? 'students' : 'users'}/${r.about._id}`}>
                          {r.about.firstName} {r.about.lastName}
                        </Link>
                      ) : (
                        r.chore?.title || t('moderation.aChore')
                      )}
                    </h3>
                    <div className="card-meta" style={{ marginTop: 4 }}>
                      <span>
                        {t('moderation.fromWhom', {
                          name: `${r.reporter?.firstName || ''} ${r.reporter?.lastName || ''}`.trim(),
                        })}
                        {reporterIsMinor
                          ? t('moderation.agedYears', { age: r.reporter.age })
                          : ''}
                      </span>
                      <span>{timeAgo(r.createdAt)}</span>
                    </div>
                  </div>
                </div>

                {r.details && (
                  <div className="card" style={{ background: 'var(--color-bg-subtle)', boxShadow: 'none', marginTop: 'var(--space-xs)' }}>
                    <p className="text-sm">{r.details}</p>
                  </div>
                )}

                {r.outcome && (
                  <p className="text-sm text-muted" style={{ marginTop: 'var(--space-xs)' }}>
                    <strong>{t('moderation.outcomeLabel')}</strong> {r.outcome}
                  </p>
                )}

                <div className="card-footer">
                  <span className="text-sm text-muted">
                    {t('moderation.statusLabel', { status: r.status })}
                  </span>
                  <div className="row" style={{ gap: 'var(--space-xs)' }}>
                    {/**
                      * The evidence, on the thing it is evidence for.
                      *
                      * A report filed from a profile names a person and no
                      * chore, which is how most of them arrive - so until
                      * this existed there was no way from a report to the
                      * conversation it was about. The server works out which
                      * threads belong to a report and checks again when one
                      * is opened; this only draws the buttons.
                      */}
                    {(r.threads || []).map((th) => (
                      <button
                        key={th._id}
                        type="button"
                        className="btn btn-outline-blue btn-sm btn-inline"
                        onClick={() => setReadingThread(th.kind === 'offer'
                          ? { offerId: th.offerId, withPoster: th.withPoster }
                          : { choreId: th.choreId || th._id, withStudent: th.withStudent })}
                      >
                        <MessageSquare size={16} /> {t('moderation.readConversation')}
                      </button>
                    ))}
                    {/* Taking the thing down is usually the proportionate
                        answer, and suspending the person is not - so it is
                        offered first. */}
                    {r.chore && (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm btn-inline"
                        onClick={() => setTakingDown({ kind: 'chore', id: r.chore._id })}
                        disabled={busy}
                      >
                        <EyeOff size={16} /> {t('moderation.takeDownChore')}
                      </button>
                    )}
                    {r.about?.role === 'student' && (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm btn-inline"
                        onClick={() =>
                          setTakingDown({ kind: 'offer', id: r.about._id, name: r.about.firstName })
                        }
                        disabled={busy}
                      >
                        <EyeOff size={16} /> {t('moderation.takeDownOffer')}
                      </button>
                    )}
                    {r.about && r.about.isActive && (
                      <button type="button" className="btn btn-danger btn-sm btn-inline" onClick={() => setSuspending(r.about)} disabled={busy}>
                        <Ban size={16} /> {t('moderation.suspend')}
                      </button>
                    )}
                    {r.about && !r.about.isActive && (
                      <button type="button" className="btn btn-outline btn-sm btn-inline" onClick={() => reinstate(r)} disabled={busy}>
                        <RotateCcw size={16} /> {t('moderation.reinstate')}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-outline-blue btn-sm btn-inline"
                      onClick={() => { setActing({ report: r }); setOutcome(r.outcome || ''); }}
                    >
                      {t('moderation.recordDecision')}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={Boolean(acting)}
        onClose={() => setActing(null)}
        title={t('moderation.whatDidYouDecide')}
        actions={
          <>
            <button type="button" className="btn btn-ghost btn-inline" onClick={() => setActing(null)}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn-outline btn-inline" onClick={() => handle('investigating')} disabled={busy}>
              {t('moderation.lookingIntoIt')}
            </button>
            <button type="button" className="btn btn-ghost btn-inline" onClick={() => handle('dismissed')} disabled={busy}>
              {t('moderation.dismiss')}
            </button>
            <button type="button" className="btn btn-primary btn-inline" onClick={() => handle('resolved')} disabled={busy}>
              {t('moderation.resolved')}
            </button>
          </>
        }
      >
        <div className="field">
          <label htmlFor="outcome">{t('misc.whatYouDid')}</label>
          <textarea
            id="outcome"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            maxLength={2000}
            placeholder={t('moderation.outcomePlaceholder')}
          />
          <span className="hint">
            {t('moderation.outcomeHint')}
          </span>
        </div>
      </Modal>
      </>
      )}

      {/* Outside the tab blocks on purpose - the dialog belongs to the page,
          not to whichever list happened to open it. */}
      <SuspendDialog
        open={Boolean(suspending)}
        person={suspending}
        busy={busy}
        onClose={() => setSuspending(null)}
        onConfirm={suspend}
      />

      <TakedownDialog
        open={Boolean(takingDown)}
        kind={takingDown?.kind}
        name={takingDown?.name}
        busy={busy}
        onClose={() => setTakingDown(null)}
        onConfirm={takedown}
      />
    </div>
  );
}
